import { Container } from "@dinahmoe/dmafjs-visual";
import { coreEngine, bridge, nodes, addChildListener } from "@dinahmoe/dmafjs-core";

class Menu extends Container {
  namespace = "visual:menu";

  handlers = {
    routes: this.handleRoutesChange
  };

  init(node, event) {
    node.events = [{ type: "up" }, { type: "over" }, { type: "out" }];
    super.init(node, event);

    node._menuElements = [];
    node._activeRouteId = null;
    node._event = event;
    node._initialized = false;
    node._pill = null;

    if (!node.navigationUid) {
      node.navigationUid = "se26_navigation";
    }

    if (node.routes && node.routes.length > 0) {
      this.buildMenu(node, event);

      const navNode = nodes.get(node.navigationUid);
      if (navNode?.currentRouteId) {
        this.setActiveMenuItem(node, navNode.currentRouteId, false);
      }
    }
  }

  handleRoutesChange(node) {
    if (!node._initialized && node.routes && node.routes.length > 0) {
      this.buildMenu(node, node._event);
    }
  }

  setRoute(node, config, event) {
    const { pathname, source } = event.data || {};
    if (!pathname || !node.routes) return;

    if (source === "overlay") return;

    // Exact match first, then prefix match for sub-routes
    let route = node.routes.find(r => r.path === pathname);
    if (!route) {
      route = node.routes.find(r => r.path !== "/" && pathname.startsWith(r.path));
    }
    if (route?.id) {
      this.setActiveMenuItem(node, route.id);
    }
  }

  buildMenu(node, event) {
    if (node._initialized) return;
    node._initialized = true;

    // Filter to only navigable routes with labels
    const menuRoutes = node.routes.filter((route) => {
      if (!route.label) return false;
      if (route.action && route.action !== "navigate") return false;
      if (route.submenu) return false;
      return true;
    });

    if (menuRoutes.length === 0) return;

    const itemHeight = 42;
    const itemSpacing = 0;

    // Calculate widths
    const menuItems = menuRoutes.map((route) => ({
      label: route.label,
      id: route.id,
      path: route.path,
      external: route.external || false,
      width: this.calculateWidth(route.label)
    }));

    // Calculate total width
    let totalWidth = 0;
    menuItems.forEach((item, index) => {
      totalWidth += item.width;
      if (index < menuItems.length - 1) {
        totalWidth += itemSpacing;
      }
    });

    // Position items with equal padding on all sides
    const verticalPadding = node['size.height'] - itemHeight;
    let currentX = verticalPadding / 2;

    // Create the sliding pill background
    node._pill = coreEngine.create({
      namespace: "visual:shape",
      parentUid: node.uid,
      uid: "menu_pill",
      size: { width: menuItems[0].width, height: itemHeight },
      position: { x: currentX },
      anchor: { x: "left", y: "center" },
      appearance: { background: node.activeColor || "#ffffff" },
      border: { radius: itemHeight / 2 }
    }, event);

    for (let i = 0; i < menuItems.length; i++) {
      const item = menuItems[i];

      const menuGroup = coreEngine.create(
        {
          namespace: "visual:group",
          parentUid: node.uid,
          uid: `menu_item_${i}`,
          size: { width: item.width, height: itemHeight },
          position: { x: currentX },
          anchor: { x: "left", y: "center" },
          events: [
            { type: "over" },
            { type: "out" },
            {
              type: "up",
              routeId: item.id,
              navigationUid: node.navigationUid,
              data: { source: "menu" }
            }
          ],
          children: [
            {
              namespace: "visual:text",
              uid: `menu_item_text_${i}`,
              text: { content: item.label, color: node.textColor || "#ffffff", lineHeight: itemHeight, align: "center" },
              font: { family: "n27-medium", size: 14 },
              size: { width: item.width },
              anchor: { x: "center", y: "center" }
            }
          ]
        },
        event
      );

      addChildListener(menuGroup, node.uid);
      menuGroup._routeId = item.id;
      menuGroup._itemIndex = i;
      menuGroup._menuX = currentX;
      menuGroup._menuWidth = item.width;
      node._menuElements.push(menuGroup);

      currentX += item.width + itemSpacing;
    }

    // Resize parent container to fit items with equal padding on all sides
    const parentNode = nodes.get(node.parentUid);
    if (parentNode) {
      parentNode['size.width'] = totalWidth + verticalPadding;
    }
  }

  receiveMessage(cNode, message) {
    if (message.uid?.startsWith("menu_item_")) {
      if (message.key === "up") {
        const menuItem = cNode._menuElements?.find((item) => item.uid === message.uid);
        if (menuItem && menuItem._routeId) {
          this.setActiveMenuItem(cNode, menuItem._routeId);
        }
      } else if (message.key === "over") {
        const menuItem = cNode._menuElements?.find((item) => item.uid === message.uid);
        if (menuItem) {
          cNode._hoveredRouteId = menuItem._routeId;
          this.updatePill(cNode);
        }
      } else if (message.key === "out") {
        const outItem = cNode._menuElements?.find((item) => item.uid === message.uid);
        if (outItem && outItem._routeId === cNode._hoveredRouteId) {
          cNode._hoveredRouteId = null;
          this.updatePill(cNode);
        }
      }
    }

    super.receiveMessage(cNode, message);
  }

  setHoverTextColors(node, highlightRouteId) {
    node._menuElements.forEach((menuElement) => {
      const textNode = nodes.get(`menu_item_text_${menuElement._itemIndex}`);
      if (textNode) {
        const targetColor = menuElement._routeId === highlightRouteId
          ? node.activeTextColor || "#CD288C"
          : node.textColor || "#ffffff";
        coreEngine.update({
          uid: textNode.uid,
          key: "transform",
          value: { transforms: [{
            property: "text.color",
            valueEnd: targetColor,
            duration: 300,
            easing: "EASE_IN_OUT"
          }] }
        });
      }
    });
  }

  animatePill(node, x, width) {
    if (!node._pill) return;
    coreEngine.update({
      uid: node._pill.uid,
      key: "transform",
      value: { transforms: [
        { property: "position.x", valueEnd: x, duration: 300, easing: "EASE_IN_OUT", curve: 30 },
        { property: "size.width", valueEnd: width, duration: 300, easing: "EASE_IN_OUT", curve: 30 }
      ] }
    });
  }

  setActiveMenuItem(node, routeId, animate = true) {
    node._activeRouteId = routeId;
    this.updatePill(node, animate);
  }

  updatePill(node, animate = true) {
    const targetRouteId = node._hoveredRouteId || node._activeRouteId;
    if (targetRouteId === node._pillTargetRouteId) return;
    node._pillTargetRouteId = targetRouteId;
    const target = node._menuElements.find(el => el._routeId === targetRouteId);
    if (!target) return;

    if (node._pill) {
      if (animate) {
        this.animatePill(node, target._menuX, target._menuWidth);
      } else {
        coreEngine.update({ uid: node._pill.uid, key: "position.x", value: target._menuX });
        coreEngine.update({ uid: node._pill.uid, key: "size.width", value: target._menuWidth });
      }
    }

    this.setHoverTextColors(node, targetRouteId);
  }

  calculateWidth(label) {
    const estimatedWidth = label.length * 8 + 48;
    return Math.ceil(estimatedWidth / 5) * 5;
  }
}

const menu = new Menu();
export { menu, Menu };
